import React, { useEffect, useMemo, useState } from 'react';
import { Target, TrendingUp, Pencil, Users } from 'lucide-react';
import { IOSBadge, IOSButton, IOSInput } from '../ui/custom';
import { useTheme } from '../../contexts/ThemeContext';
import { PageLayout, PageHeader, PageCardSection, pageStyles } from '../PageLayout';

const formatLastUpdated = (goal: any) => {
    if (!goal?.lastUpdated) return null;
    try {
        const d = new Date(goal.lastUpdated);
        if (isNaN(d.getTime())) return null;
        const dateStr = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
        return goal.updatedBy ? `Zuletzt geändert: ${dateStr} von ${goal.updatedBy}` : `Zuletzt geändert: ${dateStr}`;
    } catch {
        return null;
    }
};

const GoalsView = React.memo(({ goals, users, currentUser, onEditGoal, onSaveGoal }: any) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const currentYear = new Date().getFullYear();
    const [editingUserId, setEditingUserId] = useState<string | null>(null);
    const [editValues, setEditValues] = useState<any>({});
    const [teamTargetInput, setTeamTargetInput] = useState<string>('');
    
    // Hochrechnung: linear aufs Jahr – (Stand / vergangene Tage) × Tage im Jahr
    const calculateProjection = (goal: any) => {
        if (!goal) return null;
        const today = new Date();
        const year = today.getFullYear();
        const startOfYear = new Date(year, 0, 0).getTime();
        const dayOfYear = Math.max(1, Math.floor((today.getTime() - startOfYear) / 86400000));
        const daysInYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 366 : 365;
        const projectedAnnual = (goal.current / dayOfYear) * daysInYear;
        return Math.round(projectedAnnual);
    };

    // Soll bis heute: wie viele WS der Mitarbeiter heute haben sollte, um das Jahresziel bis Jahresende zu erreichen
    const getTargetWSByToday = (goal: any) => {
        if (!goal?.target) return null;
        const today = new Date();
        const year = today.getFullYear();
        const startOfYear = new Date(year, 0, 0).getTime();
        const dayOfYear = Math.max(1, Math.floor((today.getTime() - startOfYear) / 86400000));
        const daysInYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 366 : 365;
        return Math.round((goal.target * dayOfYear) / daysInYear);
    };
    
    const goalsWithUserInfo = useMemo(() => {
        return users
            .map((user: any) => {
                const goal = goals.find((g: any) => g.userId === user.id && g.year === currentYear);
                const progress = goal ? Math.round((goal.current / goal.target) * 100) : 0;
                const projection = calculateProjection(goal);
                const targetWSByToday = getTargetWSByToday(goal);
                return { user, goal, progress, projection, targetWSByToday };
            })
            .filter((item: any) => item.goal != null)
            .sort((a: any, b: any) => (b.goal?.current ?? 0) - (a.goal?.current ?? 0));
    }, [goals, users, currentYear]);

    const teamGoal = useMemo(() => {
        const teamGoalDoc = goals.find((g: any) => g.userId === 'team' && g.year === currentYear);
        if (!teamGoalDoc) return null;
        const teamProgress = Math.round((teamGoalDoc.current / teamGoalDoc.target) * 100);
        const teamProjection = calculateProjection(teamGoalDoc);
        return { goal: teamGoalDoc, progress: teamProgress, projection: teamProjection };
    }, [goals, currentYear]);

    const aggregatedTeamTotals = useMemo(() => {
        let currentSum = 0;
        let targetSum = 0;
        goals.forEach((g: any) => {
            if (g.year === currentYear && g.userId !== 'team') {
                currentSum += Number(g.current) || 0;
                targetSum += Number(g.target) || 0;
            }
        });
        return { currentSum, targetSum };
    }, [goals, currentYear]);

    useEffect(() => {
        if (teamGoal?.goal?.target) {
            setTeamTargetInput(String(teamGoal.goal.target));
        }
    }, [teamGoal]);

    const startEditing = (item: any) => {
        setEditingUserId(item.user.id);
        setEditValues({
            target: item.goal?.target || '',
            current: item.goal?.current || '',
            description: item.goal?.description || ''
        });
    };

    const cancelEditing = () => {
        setEditingUserId(null);
        setEditValues({});
    };

    const handleSave = (item: any) => {
        const isAdminOrGoalManager = currentUser.role === 'admin' || (currentUser as any)?.canManageGoals;
        const isOwnCurrentOnly = !isAdminOrGoalManager && item.user.id === currentUser.id;
        if (isOwnCurrentOnly && (editValues.current === '' || editValues.current == null)) return;
        if (!isOwnCurrentOnly && (!editValues.target || editValues.current === '')) return;
        const payload = isOwnCurrentOnly
            ? { target: Number(item.goal.target), current: parseFloat(String(editValues.current)), description: item.goal.description || '' }
            : { target: parseFloat(String(editValues.target)), current: parseFloat(String(editValues.current)), description: editValues.description || '' };
        onSaveGoal(item.user, item.goal, payload);
        cancelEditing();
    };

    const handleSaveTeamGoal = () => {
        const isAdminOrGoalManager = currentUser.role === 'admin' || (currentUser as any)?.canManageGoals;
        if (!isAdminOrGoalManager) return;
        const targetValue = parseFloat(teamTargetInput) || 0;
        onSaveGoal(
            { id: 'team', name: 'Gesamtteam' },
            teamGoal?.goal,
            {
                target: targetValue,
                current: aggregatedTeamTotals.currentSum,
                description: 'Teamziel'
            }
        );
    };

    const effectiveTeamTarget = teamGoal?.goal?.target || (parseFloat(teamTargetInput) || 0);
    const teamProgress = effectiveTeamTarget > 0
        ? Math.round((aggregatedTeamTotals.currentSum / effectiveTeamTarget) * 100)
        : 0;
    const teamTargetWSByToday = effectiveTeamTarget > 0 ? getTargetWSByToday({ target: effectiveTeamTarget }) : null;
    const teamProjectionColor = (() => {
        if (!teamGoal?.projection || !effectiveTeamTarget) {
            return isDark ? 'text-slate-400' : 'text-slate-500';
        }
        const ratio = teamGoal.projection / effectiveTeamTarget;
        if (ratio >= 1) return isDark ? 'text-emerald-400' : 'text-emerald-600';
        if (ratio >= 0.8) return isDark ? 'text-amber-400' : 'text-amber-600';
        return isDark ? 'text-red-400' : 'text-red-600';
    })();

    const progressBadge = (progress: number) => {
        let badgeClass = 'bg-green-500 text-white';
        if (progress < 50) badgeClass = 'bg-red-500 text-white';
        else if (progress < 75) badgeClass = 'bg-orange-500 text-white';
        return `rounded-full px-2 py-1 text-xs font-bold ${badgeClass}`;
    };

    return (
        <PageLayout>
            <div className="animate-fade-in flex flex-col h-full w-full pb-10">
            <PageHeader title="Ziele" subtitle={`Jahresziele ${currentYear} · Sortiert nach geschriebenen WS`} />

            <PageCardSection title="Teamübersicht" icon={Users}>
                    <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
                        <div>
                            <p className={`text-xs font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Geschrieben / Ziel</p>
                            <p className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                {aggregatedTeamTotals.currentSum.toLocaleString('de-DE')} <span className="font-normal text-base">/</span> {Math.max(effectiveTeamTarget, 0).toLocaleString('de-DE')} <span className={`text-sm font-normal ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>WS</span>
                            </p>
                            {teamTargetWSByToday != null && (
                                <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                    Soll bis heute: <span className={`font-semibold tabular-nums ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{teamTargetWSByToday.toLocaleString('de-DE')} WS</span>
                                </p>
                            )}
                        </div>
                        {teamGoal?.projection != null && (
                            <div className="text-right">
                                <p className={`text-xs font-medium uppercase tracking-wider mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Hochrechnung Jahresende</p>
                                <p className={`text-lg font-bold ${teamProjectionColor}`}>{teamGoal.projection.toLocaleString('de-DE')} WS</p>
                            </div>
                        )}
                    </div>
                    <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                            <span className={isDark ? 'text-slate-400' : 'text-slate-600'}>Fortschritt</span>
                            <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{teamProgress}%</span>
                        </div>
                        <div className={`rounded-full h-3 overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                            <div
                                className="h-full bg-gradient-to-r from-emerald-400 to-blue-500 transition-all duration-500"
                                style={{ width: `${Math.min(teamProgress, 100)}%` }}
                            />
                        </div>
                    </div>
                    {currentUser.role === 'admin' && (
                        <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-600/50 grid gap-3 sm:grid-cols-[1fr_auto] items-end">
                            <div>
                                <label className={`text-xs font-semibold uppercase tracking-wider block mb-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Teamziel (WS) anpassen</label>
                                <IOSInput
                                    type="number"
                                    placeholder="z.B. 500.000"
                                    value={teamTargetInput}
                                    onChange={(e: any) => setTeamTargetInput(e.target.value)}
                                />
                            </div>
                            <IOSButton onClick={handleSaveTeamGoal} disabled={!teamTargetInput}>Speichern</IOSButton>
                        </div>
                    )}
            </PageCardSection>

            <PageCardSection title="Mitarbeiterziele" icon={Target}>
                {goalsWithUserInfo.length === 0 ? (
                    <div className={pageStyles.empty(isDark)}>
                        <Target size={48} className={`mx-auto mb-4 opacity-50 ${isDark ? 'text-slate-500' : 'text-slate-300'}`} />
                        <p className="font-medium">Keine Mitarbeiter mit Ziel</p>
                        <p className="text-sm mt-1">Nur Mitarbeiter mit einem Jahresziel werden angezeigt. Admins können Ziele anlegen und bearbeiten.</p>
                    </div>
                ) : (
                    <>
                        {/* Tabellen-Header (ab md sichtbar) – immer 6 Spalten */}
                        <div
                            className={`hidden md:grid py-3 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400 bg-slate-800/50' : 'text-slate-500 bg-slate-50'}`}
                            style={{
                                gridTemplateColumns: '32px 1fr 100px 100px 90px 110px',
                                paddingLeft: '1.5rem',
                                paddingRight: '1.5rem',
                                gap: '1rem',
                            }}
                        >
                            <span>#</span>
                            <span>Mitarbeiter</span>
                            <span className="text-right">Geschrieben</span>
                            <span className="text-right">Ziel</span>
                            <span className="text-right">Fortschritt</span>
                            <span className="text-right">Hochrechnung</span>
                        </div>
                        <div className={`divide-y ${isDark ? 'divide-slate-700/50' : 'divide-slate-100'}`}>
                            {goalsWithUserInfo.map((item: any, index: number) => {
                                const { user, goal, progress, projection, targetWSByToday } = item;
                                const isEditing = editingUserId === user.id;
                                const rank = index + 1;
                                let progressColor = "bg-green-500";
                                if (progress < 50) progressColor = "bg-red-500";
                                else if (progress < 75) progressColor = "bg-orange-500";
                                else if (progress < 100) progressColor = "bg-blue-500";

                                const projectionColor = (() => {
                                    if (!projection || !goal?.target) {
                                        return isDark ? 'text-slate-500' : 'text-slate-400';
                                    }
                                    const ratio = projection / goal.target;
                                    if (ratio >= 1) return isDark ? 'text-emerald-400' : 'text-emerald-600';
                                    if (ratio >= 0.8) return isDark ? 'text-amber-400' : 'text-amber-600';
                                    return isDark ? 'text-red-400' : 'text-red-600';
                                })();

                                const isAdminOrGoalManager = currentUser.role === 'admin' || (currentUser as any)?.canManageGoals;
                                const canEditRow = isAdminOrGoalManager || user.id === currentUser.id;
                                const isOwnCurrentOnly = !isAdminOrGoalManager && user.id === currentUser.id;

                                if (isEditing) {
                                    return (
                                        <div key={user.id} className={`p-4 sm:p-6 ${isDark ? 'bg-blue-900/20 border-l-4 border-blue-500' : 'bg-blue-50/80 border-l-4 border-blue-500'}`}>
                                            <div className="mb-4">
                                                <p className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{user.name}</p>
                                                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{isOwnCurrentOnly ? 'Geschriebene WS aktualisieren' : 'Ziel bearbeiten'}</p>
                                            </div>
                                            <div className={`space-y-4 p-4 rounded-xl ${isDark ? 'bg-slate-800/50' : 'bg-white'} border ${isDark ? 'border-slate-600/50' : 'border-slate-200'}`}>
                                                {isOwnCurrentOnly ? (
                                                    <div>
                                                        <label className={`text-xs font-semibold uppercase tracking-wider block mb-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Aktuell geschrieben (WS)</label>
                                                        <IOSInput
                                                            type="number"
                                                            placeholder="z.B. 35.000"
                                                            value={editValues.current}
                                                            onChange={(e: any) => setEditValues({ ...editValues, current: e.target.value })}
                                                            autoFocus
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                        <div>
                                                            <label className={`text-xs font-semibold uppercase tracking-wider block mb-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Jahresziel (WS)</label>
                                                            <IOSInput
                                                                type="number"
                                                                placeholder="z.B. 50.000"
                                                                value={editValues.target}
                                                                onChange={(e: any) => setEditValues({ ...editValues, target: e.target.value })}
                                                                autoFocus
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className={`text-xs font-semibold uppercase tracking-wider block mb-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Aktuell geschrieben (WS)</label>
                                                            <IOSInput
                                                                type="number"
                                                                placeholder="z.B. 35.000"
                                                                value={editValues.current}
                                                                onChange={(e: any) => setEditValues({ ...editValues, current: e.target.value })}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="flex gap-3 pt-1">
                                                    <IOSButton className="flex-1" variant="ghost" onClick={cancelEditing}>Abbrechen</IOSButton>
                                                    <IOSButton
                                                        className="flex-1"
                                                        onClick={() => handleSave(item)}
                                                        disabled={isOwnCurrentOnly ? (editValues.current === '' || editValues.current == null) : (!editValues.target || editValues.current === '')}
                                                    >
                                                        Speichern
                                                    </IOSButton>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                return (
                                    <div
                                        key={user.id}
                                        role={canEditRow ? 'button' : undefined}
                                        onClick={() => canEditRow && startEditing(item)}
                                        className={`w-full text-left flex flex-col md:grid gap-3 md:gap-4 md:items-center py-4 px-4 sm:px-6 md:py-4 ${canEditRow ? `cursor-pointer rounded-none ${isDark ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50'}` : ''} transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}
                                        style={{
                                            gridTemplateColumns: '32px 1fr 100px 100px 90px 110px',
                                            gap: '1rem',
                                        }}
                                    >
                                        <span className={`hidden md:flex w-8 h-8 rounded-full items-center justify-center text-sm font-bold shrink-0 ${isDark ? 'bg-slate-600 text-slate-300' : 'bg-slate-200 text-slate-700'}`}>{rank}</span>
                                        <div className="min-w-0 flex-1 md:flex-initial">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className={`font-semibold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{user.name}</p>
                                                {canEditRow && (
                                                    <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                                        <Pencil size={12} /> {isOwnCurrentOnly ? 'Aktualisieren' : 'Bearbeiten'}
                                                    </span>
                                                )}
                                            </div>
                                            {goal ? (
                                                <>
                                                    <div className={`mt-2 rounded-full h-2 overflow-hidden ${isDark ? 'bg-slate-600' : 'bg-slate-200'}`}>
                                                        <div className={`h-full transition-all duration-300 ${progressColor}`} style={{ width: `${Math.min(progress, 100)}%` }} />
                                                    </div>
                                                    {targetWSByToday != null && (
                                                        <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                                            Soll bis heute: <span className={`font-semibold tabular-nums ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{targetWSByToday.toLocaleString('de-DE')} WS</span>
                                                        </p>
                                                    )}
                                                    <p className={`md:hidden text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                                        {goal.current.toLocaleString('de-DE')} / {goal.target.toLocaleString('de-DE')} WS · Hochr.{' '}
                                                        {projection != null ? (
                                                            <span className={projectionColor}>{projection.toLocaleString('de-DE')} WS</span>
                                                        ) : (
                                                            '–'
                                                        )}
                                                    </p>
                                                    {formatLastUpdated(goal) && (
                                                        <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-500'}`} title={formatLastUpdated(goal) ?? undefined}>
                                                            {formatLastUpdated(goal)}
                                                        </p>
                                                    )}
                                                </>
                                            ) : (
                                                <p className={`text-xs italic mt-1 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Kein Ziel</p>
                                            )}
                                        </div>
                                        {/* Desktop-Spalten */}
                                        {goal ? (
                                            <>
                                                <span className={`hidden md:block text-right font-medium tabular-nums ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>{goal.current.toLocaleString('de-DE')} WS</span>
                                                <span className={`hidden md:block text-right tabular-nums ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{goal.target.toLocaleString('de-DE')} WS</span>
                                                <span className="hidden md:flex justify-end"><span className={`px-2.5 py-1 rounded-full text-xs font-bold ${progressBadge(progress)}`}>{progress}%</span></span>
                                                <span className={`hidden md:block text-right font-medium tabular-nums ${projectionColor}`}>{projection?.toLocaleString('de-DE') ?? '–'} WS</span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="hidden md:block text-right text-slate-400">–</span>
                                                <span className="hidden md:block text-right text-slate-400">–</span>
                                                <span className="hidden md:flex justify-end"><span className={`px-2.5 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-slate-600 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>Kein Ziel</span></span>
                                                <span className="hidden md:block text-right text-slate-400">–</span>
                                            </>
                                        )}
                                        {/* Mobile: Prozent-Badge rechts */}
                                        {goal && (
                                            <span className={`md:hidden self-start ${progressBadge(progress)}`}>{progress}%</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </PageCardSection>
            </div>
        </PageLayout>
    );
});

export default GoalsView;
